/**
 * Browser/device context for a bug report, collected so the reporter never has
 * to copy it out of their own settings.
 *
 * Every read is guarded: this runs in whatever browser the person is using,
 * including the in-app webviews where `Intl`, `screen` or `matchMedia` can be
 * missing or throw. A report must never be lost because we failed to describe
 * the machine it came from — an absent field is fine, a thrown error is not.
 */

import type { BugReportClientContext } from "./report";

function safe<T>(read: () => T): T | undefined {
  try {
    return read();
  } catch {
    return undefined;
  }
}

/**
 * Snapshot the current page and device. `extra` carries facts the caller
 * already resolved (PostHog's device classification), so this module stays
 * free of analytics imports.
 */
export function collectBugReportContext(
  extra: Partial<BugReportClientContext> = {},
): BugReportClientContext {
  if (typeof window === "undefined") return { ...extra };

  const context: BugReportClientContext = {
    pageUrl: safe(() => window.location.href),
    pagePath: safe(() => window.location.pathname + window.location.search),
    referrer: safe(() => document.referrer) || undefined,
    userAgent: safe(() => navigator.userAgent),
    language: safe(() => navigator.language),
    viewport: safe(() => `${window.innerWidth}×${window.innerHeight}`),
    screen: safe(() => `${window.screen.width}×${window.screen.height}`),
    devicePixelRatio: safe(() => window.devicePixelRatio),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    online: safe(() => navigator.onLine),
    theme: safe(() =>
      document.documentElement.classList.contains("dark") ? "dark" : "light",
    ),
    ...extra,
  };

  // Drop the keys that came back undefined so the stored jsonb records what we
  // actually know rather than a wall of nulls.
  return Object.fromEntries(
    Object.entries(context).filter(([, v]) => v !== undefined && v !== ""),
  ) as BugReportClientContext;
}
